import type { CurrentUser } from '../types/models';
import type { UserEnd } from '../enums';

/**
 * 会话与令牌存储 — 按端命名空间隔离（admin 与 bpo 独立鉴权域，不共享登录态）。
 * 会话 30min 超时；令牌仅存 sessionStorage，降低 XSS 持久化风险。
 */

const END: UserEnd = (import.meta.env.VITE_APP_END as UserEnd) || 'ADMIN';
const NS = `dh:${END.toLowerCase()}`;

const K_TOKEN = `${NS}:token`;
const K_REFRESH = `${NS}:refresh`;
const K_USER = `${NS}:user`;
const K_EXPIRES = `${NS}:expires`;
const K_REMEMBER = `${END.toLowerCase()}:remember-account`;

function safeGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string, persist = false): void {
  try {
    if (persist) window.localStorage.setItem(key, value);
    else window.sessionStorage.setItem(key, value);
  } catch {
    /* 存储不可用时静默降级为内存态 */
  }
}

function safeRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getAccessToken(): string | null {
  const expires = Number(safeGet(K_EXPIRES) || 0);
  if (expires && expires < Date.now()) {
    clearSession();
    return null;
  }
  return safeGet(K_TOKEN);
}

export function getRefreshToken(): string | null {
  return safeGet(K_REFRESH);
}

export function saveSession(payload: {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  user: CurrentUser;
}): void {
  safeSet(K_TOKEN, payload.accessToken);
  if (payload.refreshToken) safeSet(K_REFRESH, payload.refreshToken);
  safeSet(K_EXPIRES, String(Date.now() + payload.expiresIn * 1000));
  safeSet(K_USER, JSON.stringify(payload.user));
}

export function getSessionUser(): CurrentUser | null {
  const raw = safeGet(K_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

/** §6.2-7 刷新后重建权限态时只更新身份快照，不动令牌与会话时限 */
export function saveSessionUser(user: CurrentUser): void {
  safeSet(K_USER, JSON.stringify(user));
}

export function sessionExpiresAt(): number {
  return Number(safeGet(K_EXPIRES) || 0);
}

export function clearSession(): void {
  [K_TOKEN, K_REFRESH, K_USER, K_EXPIRES].forEach(safeRemove);
}

/** 记住用户名（跨会话，仅账号不含密码） */
export function getRememberedAccount(): string {
  try {
    return window.localStorage.getItem(K_REMEMBER) || '';
  } catch {
    return '';
  }
}

export function setRememberedAccount(account: string): void {
  try {
    if (account) window.localStorage.setItem(K_REMEMBER, account);
    else window.localStorage.removeItem(K_REMEMBER);
  } catch {
    /* ignore */
  }
}

/** 登录失败锁定（5 次失败锁 15min）：前端倒计时提示，权威判定在后端 */
const K_FAIL = `${NS}:fail`;
export const ACCOUNT_LOCK_MINUTES = 15;

export function recordLoginFailure(unlockAt?: string): { count: number; lockedUntil: number } {
  const count = Number(safeGet(K_FAIL) || 0) + 1;
  safeSet(K_FAIL, String(count));
  const stamp = Date.now() + ACCOUNT_LOCK_MINUTES * 60 * 1000;
  const declared = unlockAt ? new Date(unlockAt).getTime() : 0;
  const lockedUntil = Number.isFinite(declared) && declared > 0 ? declared : count >= 5 ? stamp : 0;
  if (lockedUntil) safeSet(`${NS}:locked`, String(lockedUntil));
  return { count, lockedUntil };
}

export function resetLoginFailure(): void {
  safeRemove(K_FAIL);
  safeRemove(`${NS}:locked`);
}

export function lockedUntil(): number {
  const value = Number(safeGet(`${NS}:locked`) || 0);
  return value > Date.now() ? value : 0;
}
